import { Fragment, useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useAssistantStore, type ChatMessage } from '@/stores/assistant-store'
import { ArtifactSheet } from '@/components/assistant/ArtifactPanel'
import { useWideViewport } from '@/hooks/useDockedPreview'
import { useArtifactSurface } from '@/components/assistant/artifact-surface'
import { countWords, sketchDocument, type SketchLine } from '@/lib/artifact-preview'
import { countCitedSources } from '@/lib/cited-sources'
import { cn } from '@/lib/utils'

interface ArtifactChipProps {
  message: ChatMessage & { artifact: NonNullable<ChatMessage['artifact']> }
}

/**
 * Miniature of the draft's opening — headings, paragraphs and list items drawn
 * as bars at the widths the real lines run to. Decorative: the chip's title and
 * meta line carry the same information to a screen reader.
 */
function PageSketch({ lines }: { lines: SketchLine[] }) {
  return (
    <div
      aria-hidden="true"
      className="relative w-11 shrink-0 overflow-hidden rounded-md border border-border bg-card px-1 pt-1.5"
    >
      {lines.map((line, i) => (
        <div
          key={i}
          className={cn(
            'rounded-[1px]',
            line.kind === 'heading'
              ? 'mb-[5px] h-[3px] bg-muted-foreground/35'
              : 'mb-[3px] h-[2px] bg-muted-foreground/15',
            line.kind === 'list' && 'ml-1',
            line.short ? 'w-[62%]' : 'w-full'
          )}
        />
      ))}
      {/* Fades the clipped last line rather than cutting it mid-bar */}
      <div className="absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-card" />
    </div>
  )
}

/** Document chip on an assistant message — re-opens that turn's quick-write document. */
export function ArtifactChip({ message }: ArtifactChipProps) {
  // The assistant dock has no room for the docked panel, so it always uses the sheet.
  const isSheetOnly = useArtifactSurface() === 'sheet'
  const isWide = useWideViewport() && !isSheetOnly
  const [sheetOpen, setSheetOpen] = useState(false)
  const openArtifactFromMessage = useAssistantStore((s) => s.openArtifactFromMessage)

  const { artifact } = message
  const sketch = useMemo(() => sketchDocument(artifact.content), [artifact.content])
  const wordCount = useMemo(() => countWords(artifact.content), [artifact.content])

  // Sources the draft actually cites — the same count the panel's grounding
  // line shows once it is opened, not every source the turn fetched.
  const { sources, librarySources } = message
  const sourceCount = useMemo(
    () => countCitedSources(artifact.content, sources, librarySources),
    [artifact.content, sources, librarySources]
  )

  const meta = [
    'Draft',
    `${wordCount} ${wordCount === 1 ? 'word' : 'words'}`,
    ...(sourceCount > 0 ? [`${sourceCount} ${sourceCount === 1 ? 'source' : 'sources'}`] : []),
  ]

  const handleClick = () => {
    // Sets artifact + artifactPanelOpen. The docked panel renders on that state,
    // but only exists on a wide viewport — narrow raises the sheet instead.
    openArtifactFromMessage(message)
    if (!isWide) setSheetOpen(true)
  }

  return (
    <>
      <button
        onClick={handleClick}
        className="group mt-2 flex w-full max-w-[360px] items-stretch gap-3 rounded-xl border border-border bg-card p-2.5 text-left shadow-sm transition-[box-shadow,border-color] hover:border-muted-foreground/40 hover:shadow-md"
      >
        <PageSketch lines={sketch} />
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
          <span className="truncate text-sm font-semibold">{artifact.title}</span>
          <span className="flex items-center gap-1.5 text-meta">
            {meta.map((part, i) => (
              <Fragment key={part}>
                {i > 0 && (
                  <span aria-hidden="true" className="text-border">
                    &middot;
                  </span>
                )}
                <span>{part}</span>
              </Fragment>
            ))}
          </span>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 self-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
      {!isWide && <ArtifactSheet open={sheetOpen} onOpenChange={setSheetOpen} />}
    </>
  )
}
